import { useMemo, useState } from 'react';
import { Button, Grid, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { isEmpty } from 'lodash';

import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';
import TextField from 'common/forms/TextField';
import {
  RELAY_DELIVERY_METHOD_OPTIONS,
  RELAY_STATUS_OPTIONS,
  relayDeliveryMethodLabel,
  relayStatusLabel,
} from 'common/constants/relayOrders';
import { updateRelayQueueFilter } from 'store/thunks/workQueueThunks';

/**
 * POM-6099 — the relay queue's filters (PO request, 2026-09-22).
 *
 * Three substring matches, Relay Type and Status (POM-6279). `partnerIds` is not reachable from here.
 *
 * PARTNER IS A FREE-TEXT MATCH, not the multi-select the central fill filter uses. That filter
 * reads `partner[currentTenantId]` out of redux, which is populated for the partner-scoped screens
 * an ordinary operator uses. Relay is POM sys-admin only and cross-partner by nature, so there is
 * no single tenant whose partner list is the right set of options — and a select that silently
 * offered one tenant's partners would hide the orders this queue exists to show.
 *
 * Applying a filter RESETS TO PAGE 1 (see the thunk). Filtering while on page 3 would otherwise
 * request an offset the filtered result may not reach, and the operator would get a blank page
 * that looks like "no matches".
 *
 * POM-6279 P5 adds RELAY TYPE (Fax / API -> `deliveryMethods`). The slice's updateFilter REPLACES
 * relayQueue.filter rather than merging into it, so every apply and every clear sends the WHOLE
 * filter -- text keys and deliveryMethods together. Sending only the keys one control owns would
 * silently wipe the others: narrow to Fax, type a patient name, and both relay types come back.
 */
const FILTER_FIELDS = ['patientNameContains', 'partnerNameContains', 'drugNameContains'];

const EMPTY_FILTER = {
  patientNameContains: undefined,
  partnerNameContains: undefined,
  drugNameContains: undefined,
  deliveryMethods: undefined,
  statuses: undefined,
};

// MultiPracticeSelectField holds { value, label } objects. A stored value with no option (core
// added a method the portal has no label for) is kept as itself rather than dropped, so the next
// apply does not widen the queue behind the operator's back.
const toDeliveryMethodOptions = (deliveryMethods) =>
  (deliveryMethods ?? []).map(
    (value) =>
      RELAY_DELIVERY_METHOD_OPTIONS.find((option) => option.value === value) ?? {
        value,
        label: relayDeliveryMethodLabel(value),
      }
  );

const toDeliveryMethods = (options) =>
  isEmpty(options) ? undefined : options.map(({ value }) => value);

const toStatusOptions = (statuses) =>
  (statuses ?? []).map((value) => ({ value, label: relayStatusLabel(value) }));

const RelayOrdersFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const filter = useSelector(({ workQueue }) => workQueue.relayQueue.filter);

  // Memoised on the applied filter: final-form re-initialises whenever initialValues stops being
  // shallow-equal, and a fresh options array per render would wipe a half-typed form on any
  // unrelated store update.
  const initialValues = useMemo(
    () => ({
      ...filter,
      deliveryMethods: toDeliveryMethodOptions(filter?.deliveryMethods),
      statuses: toStatusOptions(filter?.statuses),
    }),
    [filter]
  );

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // TRIMMED, then emptied to undefined. A field the operator typed and cleared back to spaces
  // must read as "no filter" rather than as a search for whitespace — core trims too, but a
  // filter that reaches the wire as '   ' also makes the active-count badge lie about it.
  const handleApply = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateRelayQueueFilter({
        filter: {
          ...FILTER_FIELDS.reduce(
            (accumulator, field) => ({
              ...accumulator,
              [field]: formData[field]?.trim() || undefined,
            }),
            {}
          ),
          deliveryMethods: toDeliveryMethods(formData.deliveryMethods),
          statuses: toDeliveryMethods(formData.statuses),
        },
      })
    );
  };

  const handleClear = () => {
    setAnchorEl(null);
    dispatch(updateRelayQueueFilter({ filter: EMPTY_FILTER }));
  };

  const popoverId = anchorEl ? 'WorkQueue-RelayOrdersFilterButton-Popover' : undefined;

  return (
    <>
      <Button
        variant='outlined'
        color='primary'
        sx={{ height: 33 }}
        onClick={handleOpen}
        aria-label='filter relayed orders'
      >
        Filter
      </Button>

      <Popover
        id={popoverId}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Seeded from the CURRENT filter so reopening shows what is applied rather than a blank
            form, which reads as "nothing is filtered" while rows are still being withheld. */}
        <Form initialValues={initialValues} onSubmit={handleApply}>
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Grid container direction='column' gap={2} sx={{ p: 2, width: 320 }}>
                <Grid item>
                  <TextField
                    id='relay-filter-patientNameContains'
                    name='patientNameContains'
                    label='Patient Name'
                    submitOnEnter
                    maxLength={100}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    id='relay-filter-partnerNameContains'
                    name='partnerNameContains'
                    label='Partner Name'
                    submitOnEnter
                    maxLength={100}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    id='relay-filter-drugNameContains'
                    name='drugNameContains'
                    label='Drug Name'
                    submitOnEnter
                    maxLength={100}
                  />
                </Grid>
                <Grid item>
                  <MultiPracticeSelectField
                    id='relay-filter-deliveryMethods'
                    name='deliveryMethods'
                    label='Relay Type'
                    options={RELAY_DELIVERY_METHOD_OPTIONS}
                  />
                </Grid>
                <Grid item>
                  <MultiPracticeSelectField
                    id='relay-filter-statuses'
                    name='statuses'
                    label='Status'
                    options={RELAY_STATUS_OPTIONS}
                  />
                </Grid>
                <Grid item container gap={1} justifyContent='flex-end'>
                  <Button type='button' onClick={handleClear}>
                    Clear
                  </Button>
                  <Button type='submit' variant='contained'>
                    Apply
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </Popover>
    </>
  );
};

export default RelayOrdersFilterButton;
